import { useMemo } from "react";

import PropTypes from "prop-types";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Paper, Stack, Typography } from "@mui/material";

import { formatMoney } from "@/features/shared";
import { CustomDiscountField } from "@/features/ui";
import { computePurchaseReturnTotals } from "../purchase-return-totals";

export const PriceSummarySection = ({ editMode = false }) => {
  const { control, setValue } = useFormContext();

  const [
    returnableProductVariants,
    purchaseReturnExpenses,
    discountType,
    discountAmount,
    taxable,
    totalIncludesTax,
    taxPercentage,
  ] = useWatch({
    control,
    name: [
      "returnableProductVariants",
      "purchaseReturnExpenses",
      "discountType",
      "discountAmount",
      "taxable",
      "totalIncludesTax",
      "taxPercentage",
    ],
  });

  // Totals are derived, never stored: the submit payload runs the same function
  // over the same fields, so what is shown and what is sent cannot drift.
  const { subtotal, totalExpense, taxAmount, grandTotal } = useMemo(
    () =>
      computePurchaseReturnTotals({
        returnableProductVariants,
        purchaseReturnExpenses,
        discountType,
        discountAmount,
        taxable,
        totalIncludesTax,
        taxPercentage,
      }),
    [
      returnableProductVariants,
      purchaseReturnExpenses,
      discountType,
      discountAmount,
      taxable,
      totalIncludesTax,
      taxPercentage,
    ]
  );

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack spacing={1}>
        {/* Subtotal Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">{formatMoney(subtotal)}</Typography>
        </Stack>

        {/* Discount Input */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="body2">Diskon</Typography>
          <Controller
            control={control}
            name="discountAmount"
            render={({ field, fieldState }) => (
              <CustomDiscountField
                value={field.value ?? 0}
                onChange={field.onChange}
                required
                fullWidth={false}
                baseAmount={subtotal || 0}
                discountType={discountType}
                onTypeChanged={(newType) => setValue("discountType", newType)}
                helperText={fieldState.error?.message}
                error={fieldState.invalid}
                disabled={editMode}
                inputProps={{
                  style: { textAlign: "right" },
                }}
              />
            )}
          />
        </Stack>

        {/* Tax Display */}
        {taxable && (
          <Stack direction="row" sx={{
            justifyContent: "space-between"
          }}>
            <Typography variant="body2">Pajak ({taxPercentage}%)</Typography>
            <Typography variant="body2">{formatMoney(taxAmount)}</Typography>
          </Stack>
        )}

        {/* Returned Expenses Display */}
        {totalExpense > 0 && (
          <Stack direction="row" sx={{
            justifyContent: "space-between"
          }}>
            <Typography variant="body2">Biaya Lain-lain</Typography>
            <Typography variant="body2">{formatMoney(totalExpense)}</Typography>
          </Stack>
        )}

        {/* Grand Total Display */}
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Typography sx={{
            fontWeight: "bold"
          }}>Grand Total</Typography>
          <Typography sx={{
            fontWeight: "bold"
          }}>{formatMoney(grandTotal)}</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

PriceSummarySection.propTypes = {
  editMode: PropTypes.bool,
};
